/**
 * Sleeve Stars — Product card enhancement (Aritzia + Gymshark hybrid).
 *  - "% off" badge on sale items
 *  - One-line subtitle under title (handle map → description → type fallback)
 *  - Color DOTS — small solid color circles via COLOR_MAP
 *  - Interactive swatch → swap card main image (click locks, hover previews)
 *  - Slide-up "Add to Bag" Quick Add on hover (1-click, default variant)
 */
(function () {
  const SWATCH_SELECTOR = '[data-variant-img], [data-img-src]';

  function getSwatchTarget(target) {
    if (!target || !target.closest) return null;
    return target.closest(SWATCH_SELECTOR);
  }

  /* ============================================================
     WINDOW-LEVEL CLICK GUARD — runs at the highest priority.
     If a click/mousedown/touchstart originates inside a color
     swatch ([data-variant-img]), kill the event before any link
     activation can occur. preventDefault on mousedown is the key —
     it prevents the browser from even starting link activation.
     ============================================================ */
  (function installSwatchClickGuard() {
    function isSwatchEvent(e) {
      return !!getSwatchTarget(e.target);
    }
    function kill(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return false;
    }
    window.addEventListener('mousedown', function (e) {
      if (isSwatchEvent(e)) {
        kill(e);
        // Synchronously trigger the swap so the user sees instant feedback.
        var sw = getSwatchTarget(e.target);
        if (sw && window.__ssSwapCardImage) {
          try { window.__ssSwapCardImage(sw, false); } catch (err) {}
        }
      }
    }, { capture: true });
    window.addEventListener('click', function (e) {
      if (isSwatchEvent(e)) kill(e);
    }, { capture: true });
    window.addEventListener('touchstart', function (e) {
      if (isSwatchEvent(e)) {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    }, { capture: true, passive: false });
    window.addEventListener('auxclick', function (e) {
      if (isSwatchEvent(e)) kill(e);
    }, { capture: true });
  })();

  /* ============================================================
     STRUCTURAL NAV GUARD — convert card-product <a href> links
     to JS-driven navigation so the browser can't auto-navigate.
     In sandboxed Shopify iframe previews, preventDefault() is
     unreliable. Removing href entirely is the only foolproof
     way to prevent navigation on swatch clicks.
     ============================================================ */
  function neutralizeCardLink(anchor) {
    if (!anchor || anchor.dataset.ssNavGuarded) return;
    anchor.dataset.ssNavGuarded = '1';
    var href = anchor.getAttribute('href');
    if (!href || href === '#' || href.indexOf('javascript:') === 0) return;
    anchor.dataset.ssOriginalHref = href;
    anchor.removeAttribute('href');
    anchor.setAttribute('role', 'link');
    anchor.setAttribute('tabindex', '0');
    anchor.style.cursor = 'pointer';

    function go() {
      var url = anchor.dataset.ssOriginalHref;
      if (url) window.location.href = url;
    }

    function isInteractiveTarget(target) {
      if (!target || !target.closest) return false;
      return !!target.closest(
        'button, input, select, textarea, summary, [role="button"], [data-quick-add], .ss-card-quickadd, .ss-card-quickadd__btn, .quick-add, .quick-add__submit, form, label'
      );
    }

    anchor.addEventListener('click', function (e) {
      // If click came from inside a swatch, do nothing (let the swatch handler swap the image)
      if (getSwatchTarget(e.target)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // Let real controls handle their own click (do not navigate card link)
      if (isInteractiveTarget(e.target)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      e.preventDefault();
      go();
    });
    anchor.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      if (getSwatchTarget(e.target)) return;
      e.preventDefault();
      go();
    });
    // Middle-click / Cmd-click should open in new tab
    anchor.addEventListener('auxclick', function (e) {
      if (e.button !== 1) return;
      if (getSwatchTarget(e.target)) return;
      e.preventDefault();
      var url = anchor.dataset.ssOriginalHref;
      if (url) window.open(url, '_blank');
    });
  }

  function guardAllCardLinks() {
    var selectors = [
      '.card-wrapper a[href*="/products/"]',
      '.card-wrapper a.full-unstyled-link',
      '.card a[href*="/products/"]',
      '.product-card-wrapper a[href*="/products/"]',
      '.grid__item a[href*="/products/"]'
    ];
    document.querySelectorAll(selectors.join(', ')).forEach(neutralizeCardLink);
  }

  /* Run on load + after card injection */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', guardAllCardLinks);
  } else {
    guardAllCardLinks();
  }
  document.addEventListener('shopify:section:load', guardAllCardLinks);
  setTimeout(guardAllCardLinks, 400);
  setTimeout(guardAllCardLinks, 1500);

  if ('MutationObserver' in window) {
    var __ssLinkObserver = new MutationObserver(function () { guardAllCardLinks(); });
    __ssLinkObserver.observe(document.body || document.documentElement, { childList: true, subtree: true });
  }

  const MAX_VISIBLE_DOTS = 5;
  const SUBTITLE_MAX = 75;
  const cache = new Map();

  // Curated subtitle per known product handle. First match wins (substring).
  const SUBTITLE_MAP = [
    { match: 'plantar', text: 'Plantar fasciitis, heel pain & long days on your feet' },
    { match: 'ankle',   text: 'Sprains, swelling & long days on your feet' },
    { match: 'patella', text: 'Runner’s knee, kneecap pain & stair-day relief' },
    { match: 'knee',    text: 'Arthritis ache, post-surgery & walking comfort' },
    { match: 'elbow',   text: 'Tennis elbow, golfer’s elbow & tendonitis' },
    { match: 'wrist',   text: 'Carpal tunnel, typing strain & sprains' },
    { match: 'calf',    text: 'Shin splints, cramps & long-flight recovery' },
    { match: 'thigh',   text: 'Quad strains, hamstring support & post-workout' },
    { match: 'back',    text: 'Lower back support for lifting & long sits' }
  ];

  // Color-name → hex map (mirrors assets/sleevestars-pdp-visuals.js COLOR_MAP).
  var COLOR_MAP = {
    'black':'#0E1218','jet black':'#0E1218','white':'#FFFFFF','ivory':'#F5EFE0','cream':'#F4ECDA',
    'beige':'#D9C7A7','tan':'#C9A87C','nude':'#D8B89A','skin':'#E0BFA0','sand':'#E8DBC1',
    'grey':'#8C8C8C','gray':'#8C8C8C','light grey':'#C7C7C7','light gray':'#C7C7C7','charcoal':'#36383D',
    'navy blue':'#1B2A4E','navy':'#1B2A4E','royal blue':'#1F47C2','light blue':'#7FB3E8','baby blue':'#A8C9E8','blue':'#2F6FE0',
    'turquoise':'#3FB8B8','teal':'#2E8C8C','neon green':'#5BC04A','green':'#3B7A4A','olive':'#6E7A3A','mint':'#A8D8C0',
    'red':'#D8252A','crimson':'#A6171C','maroon':'#6E1F22',
    'hot pink':'#E63E80','baby pink':'#F6C6D2','pink':'#F2A6BC','rose':'#D87386','coral':'#F47B5A',
    'orange':'#F08236','yellow':'#F5C84B','mustard':'#C49A2D',
    'light purple':'#B9A1D5','dark purple':'#5B358E','purple':'#7A4DAA','lavender':'#B7A6D6','lilac':'#C9A6D4',
    'mocha brown':'#7A5A3C','dark brown':'#4A2E1A','mocha':'#7A5A3C','brown':'#6B4A2B',
    'camo':'#5A6741','camouflage':'#5A6741'
  };
  function colorFor(name) {
    if (!name) return null;
    var key = String(name).trim().toLowerCase();
    if (COLOR_MAP[key]) return COLOR_MAP[key];
    var keys = Object.keys(COLOR_MAP).sort(function (a, b) { return b.length - a.length; });
    for (var i = 0; i < keys.length; i++) { if (key.indexOf(keys[i]) !== -1) return COLOR_MAP[keys[i]]; }
    return null;
  }
  function hexLuma(hex) {
    if (!hex) return 0;
    var h = hex.replace('#', '');
    if (h.length === 3) h = h.split('').map(function (c) { return c + c; }).join('');
    var r = parseInt(h.slice(0, 2), 16) / 255;
    var g = parseInt(h.slice(2, 4), 16) / 255;
    var b = parseInt(h.slice(4, 6), 16) / 255;
    // Relative luminance (perceptual)
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  function pct(orig, sale) {
    if (!orig || !sale || orig <= sale) return null;
    return Math.round(((orig - sale) / orig) * 100);
  }

  function findVisibleHeading(card) {
    const all = card.querySelectorAll('.card__heading');
    for (let i = all.length - 1; i >= 0; i--) {
      if (!all[i].closest('.card__inner')) return all[i];
    }
    return all[all.length - 1] || null;
  }

  function applyDiscount(card, product) {
    const allBadges = card.querySelectorAll('.card__badge .badge');
    const badge = Array.from(allBadges).find(b => !b.closest('.card__inner')) || allBadges[0];
    if (!badge || !product) return;
    const v = product.variants && product.variants[0];
    if (!v) return;
    const p = pct(v.compare_at_price, v.price);
    if (p) badge.setAttribute('data-ss-discount', '-' + p + '%');
  }

  function cleanSubtitle(html) {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    let text = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
    if (!text) return '';
    const m = text.match(/^(.{20,}?[\.!?])(\s|$)/);
    if (m) text = m[1];
    text = text.replace(/\.$/, '');
    if (text.length > SUBTITLE_MAX) {
      const cut = text.slice(0, SUBTITLE_MAX);
      const lastSpace = cut.lastIndexOf(' ');
      text = (lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trim() + '…';
    }
    return text;
  }

  function deriveSubtitle(product) {
    if (product && product.handle) {
      const h = product.handle.toLowerCase();
      for (const row of SUBTITLE_MAP) {
        if (h.indexOf(row.match) !== -1) return row.text;
      }
    }
    const fromDesc = cleanSubtitle(product && product.description);
    if (fromDesc && fromDesc.length >= 14) return fromDesc;
    if (product && product.type) {
      return 'Compression support for the ' + String(product.type).toLowerCase();
    }
    return '';
  }

  function applySubtitle(card, product) {
    if (card.dataset.ssSubtitleApplied) return;
    const titleNode = findVisibleHeading(card);
    if (!titleNode) return;
    const subtitleText = deriveSubtitle(product);
    if (!subtitleText) return;
    card.dataset.ssSubtitleApplied = '1';
    const sub = document.createElement('p');
    sub.className = 'ss-card-subtitle';
    sub.textContent = subtitleText;
    if (titleNode.nextSibling) titleNode.parentNode.insertBefore(sub, titleNode.nextSibling);
    else titleNode.parentNode.appendChild(sub);
  }

  function getColorIdx(product) {
    if (!product || !product.options) return -1;
    const optNames = product.options.map(o => (typeof o === 'string' ? o : o.name));
    return optNames.findIndex(n => /colou?r/i.test(n));
  }

  function getSizeIdx(product) {
    if (!product || !product.options) return -1;
    const optNames = product.options.map(o => (typeof o === 'string' ? o : o.name));
    return optNames.findIndex(n => /size/i.test(n));
  }

  // Resize a Shopify CDN image URL to width=400 for card swaps.
  function cardSizedUrl(src) {
    if (!src) return src;
    return src.replace(/(\.(?:jpe?g|png|webp|avif))(\?|$)/i, '_400x$1$2');
  }

  // Walk up from the swatch wrap, find the closest <a> ancestor, and if
  // found, move the swatch wrap to be the next sibling of that <a>. This
  // guarantees the swatches are never inside a navigable element (Dawn's
  // card-wrapper wraps the entire card in <a href="/products/...">).
  function hoistSwatchesOutOfLink(wrap) {
    if (!wrap) return;
    var anchor = wrap.closest('a');
    if (!anchor) return; // already outside
    var parentOfAnchor = anchor.parentNode;
    if (!parentOfAnchor) return;
    if (anchor.nextSibling) {
      parentOfAnchor.insertBefore(wrap, anchor.nextSibling);
    } else {
      parentOfAnchor.appendChild(wrap);
    }
  }

  function hoistControlOutOfProductLink(node) {
    if (!node) return;
    var anchor = node.closest('a[href*="/products/"], a[data-ss-original-href*="/products/"]');
    if (!anchor) return;
    var parent = anchor.parentNode;
    if (!parent) return;
    if (anchor.nextSibling) {
      parent.insertBefore(node, anchor.nextSibling);
    } else {
      parent.appendChild(node);
    }
  }

  function hoistExistingSwatchesOutOfLinks(scope) {
    var root = scope || document;
    var wrappers = root.querySelectorAll('[data-card-swatches], .ss-card-swatches');
    wrappers.forEach(function (wrap) {
      hoistSwatchesOutOfLink(wrap);
      hoistControlOutOfProductLink(wrap);

      // Prevent first-paint vertical stacking before CSS fully applies.
      var list = wrap.querySelector('.ss-card-swatches__list');
      if (list) {
        list.style.display = 'flex';
        list.style.flexWrap = 'nowrap';
        list.style.alignItems = 'center';
        list.style.gap = '6px';
        list.style.listStyle = 'none';
        list.style.padding = '0';
        list.style.margin = '0';
      }
      wrap.querySelectorAll('.ss-card-swatches__list > li').forEach(function (li) {
        li.style.display = 'inline-flex';
        li.style.flex = '0 0 auto';
      });

      // Hydrate older markup that only has data-img-src.
      wrap.querySelectorAll('[data-img-src]').forEach(function (sw) {
        if (!sw.getAttribute('data-variant-img')) {
          var src = sw.getAttribute('data-img-src');
          if (src) sw.setAttribute('data-variant-img', cardSizedUrl(src));
        }
      });

      // Keep swatches above any link hit-area overlays.
      wrap.style.position = 'relative';
      wrap.style.zIndex = '6';
      wrap.style.pointerEvents = 'auto';
    });

    // Existing quick add controls can also be trapped inside a product link.
    root.querySelectorAll('[data-quick-add], .ss-card-quickadd').forEach(function (qa) {
      hoistControlOutOfProductLink(qa);
      qa.style.position = 'relative';
      qa.style.zIndex = '7';
      qa.style.pointerEvents = 'auto';
    });
  }

  // Per-swatch listeners — applied at element creation time. We bind in
  // CAPTURE phase so we beat any bubbling listener on a parent <a>, and
  // call stopImmediatePropagation so sibling listeners can't navigate.
  // This is a safety net on top of the existing document delegation.
  function attachSwatchHandlers(btn) {
    btn.addEventListener('mousedown', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    }, true);
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      swapCardImage(btn, false);
      return false;
    }, true);
    btn.addEventListener('touchstart', function (e) {
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    }, { passive: true, capture: true });
  }

  function buildColorDots(card, product) {
    if (!product || !product.variants || product.variants.length <= 1) return;
    if (card.querySelector('[data-card-swatches]')) return;
    const mediaWrap = card.querySelector('.card__inner') || card.querySelector('.card__media');
    if (!mediaWrap) return;
    const colorIdx = getColorIdx(product);
    let entries = [];
    if (colorIdx >= 0) {
      const seen = new Set();
      product.variants.forEach(v => {
        const val = v.options ? v.options[colorIdx] : v[`option${colorIdx + 1}`];
        if (!val || seen.has(val)) return;
        seen.add(val);
        entries.push({
          variantId: v.id,
          label: val,
          img: (v.featured_image && v.featured_image.src) || product.featured_image || null
        });
      });
    } else {
      return;
    }
    if (entries.length <= 1) return;

    const totalCount = entries.length;
    const visible = entries.slice(0, MAX_VISIBLE_DOTS);
    const overflow = totalCount - visible.length;

    const wrap = document.createElement('div');
    wrap.className = 'ss-card-swatches';
    wrap.setAttribute('data-card-swatches', '');

    const list = document.createElement('ul');
    list.className = 'ss-card-swatches__list';
    list.setAttribute('role', 'list');

    visible.forEach((e, i) => {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('aria-label', e.label);
      btn.setAttribute('data-variant-id', e.variantId);
      btn.style.pointerEvents = 'auto';
      btn.className = 'ss-card-swatches__sw' + (i === 0 ? ' is-active' : '');
      var hex = colorFor(e.label) || '#E8DBC1';
      btn.style.backgroundColor = hex;
      if (hexLuma(hex) > 0.7) btn.setAttribute('data-light', '1');
      if (e.img) {
        const cardImg = cardSizedUrl(e.img);
        btn.setAttribute('data-variant-img', cardImg);
        btn.setAttribute('data-img-src', e.img);
      } else {
        btn.setAttribute('data-variant-img', '');
      }
      attachSwatchHandlers(btn);
      li.appendChild(btn);
      list.appendChild(li);
    });

    if (overflow > 0) {
      const li = document.createElement('li');
      const more = document.createElement('span');
      more.className = 'ss-card-swatches__more';
      more.textContent = '+' + overflow;
      more.title = totalCount + ' colors total';
      li.appendChild(more);
      list.appendChild(li);
    }

    wrap.appendChild(list);
    // IMPORTANT: append as SIBLING of the card link/inner — NOT a child of <a>.
    // mediaWrap is .card__inner (sits outside the product link wrapper in Dawn).
    const next = mediaWrap.nextElementSibling;
    if (next) mediaWrap.parentNode.insertBefore(wrap, next);
    else mediaWrap.parentNode.appendChild(wrap);

    // DOM-level safety: if we still ended up inside an <a> (Dawn wraps the
    // whole card-wrapper in a product link), hoist the swatch wrap out so
    // clicks can never bubble to a navigable ancestor.
    hoistSwatchesOutOfLink(wrap);
    hoistControlOutOfProductLink(wrap);

    // Ensure the swatch layer stays above card link overlays.
    wrap.style.position = 'relative';
    wrap.style.zIndex = '6';
    wrap.style.pointerEvents = 'auto';
  }

  function buildQuickAdd(card, product) {
    // Quick add intentionally disabled.
    return;

    if (!product || !product.variants || !product.variants.length) return;
    if (card.querySelector('[data-quick-add]')) return;
    const inner = card.querySelector('.card__inner');
    if (!inner) return;

    // const wrap = document.createElement('div');
    // wrap.className = 'ss-card-quickadd';
    // wrap.setAttribute('data-quick-add', '');
    // wrap.setAttribute('data-variant-id', product.variants[0].id);

    // const btn = document.createElement('button');
    // btn.type = 'button';
    // btn.className = 'ss-card-quickadd__btn';
    // btn.textContent = 'Add to bag';

    btn.addEventListener('click', async (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      if (btn.classList.contains('is-loading') || btn.classList.contains('is-added')) return;
      const vid = wrap.getAttribute('data-variant-id');
      if (!vid) return;
      btn.classList.add('is-loading');
      btn.textContent = 'Adding…';
      try {
        const r = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(vid, 10), quantity: 1 }] })
        });
        if (!r.ok) throw new Error('add failed');
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        btn.textContent = 'Added';
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          if (typeof drawer.renderContents === 'function') {
            try {
              const cartR = await fetch('/cart.js');
              const cartJson = await cartR.json();
              drawer.renderContents({ ...cartJson, sections: {} });
            } catch (_) {}
          }
          if (typeof drawer.open === 'function') drawer.open();
        }
        setTimeout(() => { btn.classList.remove('is-added'); btn.textContent = 'Add to bag'; }, 2200);
      } catch (e) {
        btn.classList.remove('is-loading');
        btn.textContent = 'Try again';
        setTimeout(() => { btn.textContent = 'Add to bag'; }, 1800);
      }
    });

    wrap.appendChild(btn);
    inner.appendChild(wrap);

    // Keep quick add interactive even when card markup is wrapped by a link.
    hoistControlOutOfProductLink(wrap);
    wrap.style.position = 'relative';
    wrap.style.zIndex = '7';
    wrap.style.pointerEvents = 'auto';
  }

  // ----------------------------------------------------------------
  // Image-swap delegation (works on any card injected at runtime).
  //   click   → locked swap (sticks), sets .is-active
  //   hover   → temporary preview (reverts on mouseleave unless locked)
  // ----------------------------------------------------------------
  function getCardImg(card) {
    return card.querySelector(
      '.card__media img, .card-wrapper img, picture img, img.motion-reduce'
    );
  }

  function getCardForSwatch(sw) {
    return sw.closest('.product-card-wrapper, .card-wrapper, .card, .grid__item, [data-card]');
  }

  function applyImg(img, newUrl) {
    img.style.transition = 'opacity 150ms ease';
    img.style.opacity = '0';
    setTimeout(function () {
      img.src = newUrl;
      img.removeAttribute('srcset');
      img.style.opacity = '1';
    }, 150);
  }

  function swapCardImage(sw, isHover) {
    const card = getCardForSwatch(sw);
    if (!card) return;
    const img = getCardImg(card);
    if (!img) return;
    let newUrl = sw.getAttribute('data-variant-img');
    if (!newUrl) {
      const fallback = sw.getAttribute('data-img-src');
      if (fallback) {
        newUrl = cardSizedUrl(fallback);
        sw.setAttribute('data-variant-img', newUrl);
      }
    }
    if (!newUrl) return;
    if (!card.dataset.ssDefaultImg) card.dataset.ssDefaultImg = img.src;
    if (!isHover) {
      card.dataset.ssLockedImg = newUrl;
      const scope = sw.closest('.ss-card-swatches') || sw.parentElement;
      if (scope) {
        scope.querySelectorAll(SWATCH_SELECTOR).forEach(function (s) {
          s.classList.remove('is-active');
        });
      }
      sw.classList.add('is-active');
    }
    applyImg(img, newUrl);
  }
  // Expose globally so the window-level guard at the top can call it
  // synchronously on mousedown (before any link activation).
  window.__ssSwapCardImage = swapCardImage;

  function revertTempSwap(sw) {
    const card = getCardForSwatch(sw);
    if (!card) return;
    const img = getCardImg(card);
    if (!img) return;
    if (card.dataset.ssLockedImg) {
      if (img.src !== card.dataset.ssLockedImg) applyImg(img, card.dataset.ssLockedImg);
      return;
    }
    if (!card.dataset.ssDefaultImg) return;
    if (img.src === card.dataset.ssDefaultImg) return;
    applyImg(img, card.dataset.ssDefaultImg);
  }

  // Strong click suppression — capture phase + bubble phase, both stop
  // immediate propagation so the card-wide product link never fires.
  function handleSwatchClick(e) {
    const sw = getSwatchTarget(e.target);
    if (!sw) return;
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    swapCardImage(sw, false);
  }

  let swapBound = false;
  function bindSwatchSwap() {
    if (swapBound) return;
    swapBound = true;
    // Capture phase — beats any parent link/handler.
    document.addEventListener('click', handleSwatchClick, true);
    // Bubble phase — belt-and-suspenders for any handler that re-fires.
    document.addEventListener('click', handleSwatchClick, false);

    // Desktop hover preview
    document.addEventListener('mouseover', function (e) {
      const sw = getSwatchTarget(e.target);
      if (!sw) return;
      swapCardImage(sw, true);
    });
    document.addEventListener('mouseout', function (e) {
      const sw = getSwatchTarget(e.target);
      if (!sw) return;
      const to = e.relatedTarget;
      if (to && sw.contains(to)) return;
      revertTempSwap(sw);
    });

    // Keyboard a11y — Enter / Space on a focused swatch
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const sw = document.activeElement;
      if (!sw || !sw.matches || !sw.matches(SWATCH_SELECTOR)) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      swapCardImage(sw, false);
    });
  }

  async function fetchProduct(url) {
    if (cache.has(url)) return cache.get(url);
    try {
      const r = await fetch(url + '.js', { credentials: 'same-origin' });
      if (!r.ok) return null;
      const data = await r.json();
      cache.set(url, data);
      return data;
    } catch (e) { return null; }
  }

  function enhanceCard(card) {
    if (card.dataset.ssCardEnhanced) return;
    card.dataset.ssCardEnhanced = '1';
    const link = card.querySelector('a[href*="/products/"], a[data-ss-original-href*="/products/"]');
    if (!link) return;
    const hrefRaw = link.getAttribute('href') || link.dataset.ssOriginalHref || '';
    if (!hrefRaw) return;
    var pathOnly = hrefRaw.split('?')[0].split('#')[0].replace(/\/$/, '');
    fetchProduct(pathOnly).then(product => {
      if (!product) return;
      applyDiscount(card, product);
      applySubtitle(card, product);
      buildColorDots(card, product);
      // buildQuickAdd(card, product); // Add to bag removed intentionally.
    });
  }

  function init() {
    hoistExistingSwatchesOutOfLinks(document);
    document.querySelectorAll('.product-card-wrapper').forEach(enhanceCard);
    bindSwatchSwap();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
  const mo = new MutationObserver(() => init());
  if (document.body) mo.observe(document.body, { childList: true, subtree: true });
})();
